/* eslint-disable camelcase */
import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { useForm } from "react-hook-form";
import styled, { css } from "styled-components";
import { useScreenClass } from "react-grid-system";
import MinimalSelect from "./MinimalSelect";
import FlexBox, { FlexGrow } from "./FlexBox";
import theme from "../styles/theme";
import { StyledText } from "./Text";
import Button from "./Button";
import { RightAlign } from "./UtilsComponents";
import Icon, { ClickableIcon } from "./Icon";
import Status from "../types/enum/Status";
import Modal from "./Modal";
import SnackBar from "./SnackBar";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { Checkbox, Switch, TextField } from "@mui/material";
import dayjs from "dayjs";
import Table from "./Table";

const sharedMinimalStyle = css`
  width: 100%;
  border-color: hsl(0, 0%, 80%);
  border-radius: 4px;
  border-style: solid;
  border-width: 1px;
  padding: 0px 12px;
  .react-select__menu {
    width: 100%;
    min-width: unset;
    margin-left: -12px;
  }
`;
const StyledMinimalSelect = styled(MinimalSelect)`
  ${sharedMinimalStyle}
`;
type EditAliasForm = {
  source_program: string;
  activity_date: string;
  alias_program: string;
  alias_course_code: string;
  expected_graduation_term: string;
  expected_graduation_date: string;
};
type CourseEditModalProps = {
  userInfo: any;
  reloadParent: (x: string) => void;
  isOpen?: boolean;
  setOpen?: (x: boolean) => void;
  modalData: any;
  setModalData: (x: any) => void;
};

const AddProgramModal = ({
  userInfo,
  reloadParent,
  isOpen,
  setOpen,
  modalData,
  setModalData,
}: CourseEditModalProps): JSX.Element => {
  const {
    register,
    handleSubmit,
    watch,
    formState: { errors, isDirty },
  } = useForm<EditAliasForm>({});
  const screenClass = useScreenClass();
  const isMobile = useMemo(() => ["xs", "sm"].includes(screenClass), [
    screenClass,
  ]);
  const [loading, setLoading] = useState<boolean>(true);

  const [errorSnackbarOpen, setErrorSnackbarOpen] = useState<boolean>(false);
  const [programs, setPrograms] = useState([
    {
      label: "",
      value: "",
    },
  ]);
  const [terms, setTerms] = useState([
    {
      label: "",
      value: "",
    },
  ]);

  const [honors, setHonors] = useState([
    {
      label: "",
      value: "",
    },
  ]);
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [isValidDate, setIsValidDate] = useState(true);
  const [isWithTransfer, setIsWithTransfer] = useState<boolean>(false);

  useEffect(() => {
    fetchPrograms();
    fetchTerms();
    mapUserPrograms();
    // fetchHonors();
  }, []);

  const mapUserPrograms = () => {
    return userInfo.programs.map((program: any) => ({
      label: program.short_name,
      value: program.program_id,
    }));
  };
  const fetchPrograms = () => {
    setLoading(true);
    axios
      .get("/api/v1/courses/programs")
      .then((res) => {
        if (res.data.data.programs) {
          // eslint-disable-next-line max-len
          const programList: React.SetStateAction<
            { label: string; value: string }[]
          > = [];
          const excludeIds = new Set(
            mapUserPrograms().map((p: any) => p.value)
          );
          res.data.data.programs.forEach(
            (element: { name: string; id: string; short_name: string }) => {
              if (!excludeIds.has(element.id)) {
                const array = { label: element.short_name, value: element.id };
                programList.push(array);
              }
            }
          );
          setPrograms(programList);
        }
        setLoading(false);
      })
      .catch((err) => {
        setLoading(false);
      });
  };
  const onSubmitClicked = (data: any) => {
    const enrollmentTermIndex = terms.findIndex(
      (t) => t.value === modalData.data.enrollment_term_id
    );
    const expectedTermIndex = terms.findIndex(
      (t) => t.value === modalData.data.expected_graduation_term_id
    );

    if (
      enrollmentTermIndex !== -1 &&
      expectedTermIndex !== -1 &&
      expectedTermIndex < enrollmentTermIndex
    ) {
      setErrorMessage(
        "Expected Graduation Term cannot be earlier than Enrollment Term."
      );
      setErrorSnackbarOpen(true);
      return;
    }
    setLoading(true);
    axios
      .post(`/api/v1/students/programs/new`, modalData.data)
      .then((res) => {
        setLoading(false);
        reloadParent(res.data.data);
      })
      .catch((error) => {
        setErrorMessage(error?.response?.data?.error);
        setErrorSnackbarOpen(true);
        setLoading(false);
      });
  };
  const onError = (errors: any, e: any) => console.log("error", errors, e);

  const fetchTerms = () => {
    setLoading(true);
    axios
      .get("/api/v1/courses/terms")
      .then((res) => {
        if (res.data.data.terms) {
          const today = dayjs();
          const term: React.SetStateAction<
            { label: string; value: string }[]
          > = [];
          res.data.data.terms.forEach(
            (element: { name: string; id: string; term_end: string }) => {
              // Only include terms where today is before or equal to term_end
              // if (
              //   !element.term_end ||
              //   today.isBefore(dayjs(element.term_end), "day") ||
              //   today.isSame(dayjs(element.term_end), "day")
              // )
               {
                const array = { label: "", value: "" };
                array.label = element.name;
                array.value = element.id;
                term.push(array);
              }
            }
          );
          setTerms(term);
        }
        setLoading(false);
      })
      .catch((err) => {
        setLoading(false);
      });
  };
  // const fetchHonors = () => {
  //   setLoading(true);
  //   axios
  //     .get("/api/v1/courses/honors")
  //     .then((res) => {
  //       if (res.data.data.honors) {
  //         const honor: React.SetStateAction<
  //           { label: string; value: string }[]
  //         > = [];
  //         res.data.data.honors.forEach(
  //           (element: { honor_name: string; id: string }) => {
  //             const array = { label: "", value: "" };
  //             array.label = element.honor_name;
  //             array.value = element.id;
  //             honor.push(array);
  //           }
  //         );
  //         setHonors(honor);
  //       }
  //       setLoading(false);
  //     })
  //     .catch((err) => {
  //       setLoading(false);
  //     });
  // };

  const [isPreviewOpen, setIsPreviewOpen] = useState<boolean>(false);
  const [previewData, setPreviewData] = useState<any[]>([]);

  const openPreviewModal = () => {
    const enrollmentTermIndex = terms.findIndex(
      (t) => t.value === modalData.data.enrollment_term_id
    );
    const expectedTermIndex = terms.findIndex(
      (t) => t.value === modalData.data.expected_graduation_term_id
    );

    if (
      enrollmentTermIndex !== -1 &&
      expectedTermIndex !== -1 &&
      expectedTermIndex < enrollmentTermIndex
    ) {
      setErrorMessage(
        "Expected Graduation Term cannot be earlier than Enrollment Term."
      );
      setErrorSnackbarOpen(true);
      return;
    }
    setModalData({
      ...modalData,
      data: {
        ...modalData.data,
        with_transfer: true,
      },
    });
    setIsPreviewOpen(true);
    axios
      .get(
        `/api/v1/students/program-transfer/preview?student_id=${modalData?.data?.student_id}&source_program_id=${modalData?.data?.source_program_id}&target_program_id=${modalData?.data?.program_id}`
      )
      .then((res) => {
        if (res.data.result.length > 0) {
          setPreviewData(res.data.result);
        }
      })
      .catch((error) => {
        setPreviewData([]);
        // setErrorMessage("Failed to fetch preview data.");
        // setErrorSnackbarOpen(true);
      });
  };

  if (isPreviewOpen) {
    return (
      <>
        <SnackBar
          isOpen={errorSnackbarOpen}
          setOpen={setErrorSnackbarOpen}
          type="error"
          message={errorMessage}
        />
        <Modal
          isOpen={isPreviewOpen}
          setOpen={setIsPreviewOpen}
          customStyle={{
            content: { width: "100%", maxWidth: isMobile ? "100%" : "800px" },
          }}
        >
          <FlexBox
            style={{
              marginBottom: theme.spacing.base * 2,
            }}
          >
            <FlexGrow>
              <h3 style={{ marginRight: "auto", marginBottom: 0 }}>
                Preview Program Transfers
              </h3>
            </FlexGrow>
            <RightAlign>
              <ClickableIcon
                name="x-s"
                onClick={() => {
                  setModalData({
                    type: "",
                    data: {
                      student_id: modalData?.data?.student_id,
                      program_id: "",
                      activity_date: new Date().toISOString(),
                      activity_type_id: 4,
                      expected_graduation_date: "",
                      expected_graduation_term_id: "",
                      honor_id: "",
                      with_transfer: false,
                      source_program_id: "",
                      source_program_name: "",
                    },
                  });
                }}
              />
            </RightAlign>
          </FlexBox>
          <FlexBox>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                fontWeight: "bold",
                fontSize: "1.2rem",
                color: "#3d0004",
              }}
            >
              {modalData.data.source_program_name}
              <span style={{ fontWeight: "normal" }}>(Source Program)</span>
              <Icon name="arrow-right" size={2} style={{ margin: 10 }} />
              {modalData.data.program_name}
              <span style={{ fontWeight: "normal" }}>(New Program)</span>
            </div>
          </FlexBox>
          <FlexBox>
            <div
              style={{
                width: "100%",
                maxHeight: "400px", // Set your desired max height
                overflowY: "auto", // Enable vertical scroll
              }}
            >
              {/* <span>Source Course</span> <span>Credits</span> */}
              <Table style={{ width: "100%" }}>
                <thead>
                  <tr>
                    <th
                      style={{
                        position: "sticky",
                        top: 0,
                        background: "#fff",
                        zIndex: 2,
                      }}
                    >
                      Source Course
                    </th>
                    <th
                      style={{
                        position: "sticky",
                        top: 0,
                        background: "#fff",
                        zIndex: 2,
                      }}
                    >
                      Units
                    </th>
                    <th
                      style={{
                        position: "sticky",
                        top: 0,
                        background: "#fff",
                        zIndex: 2,
                      }}
                    >
                      Grade
                    </th>

                    <th
                      style={{
                        position: "sticky",
                        top: 0,
                        background: "#fff",
                        zIndex: 2,
                        border: "none",
                      }}
                    ></th>
                    <th
                      style={{
                        position: "sticky",
                        top: 0,
                        background: "#fff",
                        zIndex: 2,
                      }}
                    >
                      New Course
                    </th>
                    <th
                      style={{
                        position: "sticky",
                        top: 0,
                        background: "#fff",
                        zIndex: 2,
                      }}
                    >
                      Units
                    </th>
                    <th
                      style={{
                        position: "sticky",
                        top: 0,
                        background: "#fff",
                        zIndex: 2,
                      }}
                    >
                      Status
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {previewData.map((item, index) => (
                    <tr key={index}>
                      <td>{item.source_course_code || "--"}</td>
                      <td>{item.source_course_units}</td>
                      <td>{item.source_course_grade}</td>
                      <td style={{ border: "none" }}>
                        <Icon
                          name="arrow-right"
                          size={2}
                          style={{
                            color: item.target_course_code
                              ? "#028702"
                              : "#c10000",
                          }}
                        />
                      </td>
                      <td>{item.target_course_code || "--"}</td>
                      <td>
                        {item.target_course_code
                          ? item.target_course_units
                          : "--"}
                      </td>
                      <td
                        style={{
                          color: item.target_course_code
                            ? "#028702"
                            : "#c10000",
                        }}
                      >
                        {item.target_course_code ? (
                          <>
                            <Icon name="circle-checked" /> Transferable
                          </>
                        ) : (
                          <>
                            <Icon name="circle-x" /> Not Transferable
                          </>
                        )}
                      </td>
                    </tr>
                  ))}

                  {/* <tr>
                  <td>AIC-101</td>
                  <td>4</td>
                  <td
                    style={{
                      border: "none",
                      fontWeight: "bold",
                      color: "#028702",
                    }}
                  >
                    {" "}
                    <Icon name="arrow-right" size={2} />
                  </td>
                  <td>AIC-101(shared program)</td>
                  <td>4</td>
                  <td style={{ color: "#028702" }}>
                    {" "}
                    <Icon name="check-mark" style={{border:'1px solid #028702', borderRadius:10, margin:10}} />
                    Transferable
                  </td>
                </tr> */}
                </tbody>
              </Table>
            </div>

            <FlexBox
              alignItems="center"
              justify="flex-end"
              style={{
                width: "100%",
                marginTop: theme.spacing.x4,
                marginBottom: theme.spacing.x2,
              }}
            >
              <form onSubmit={handleSubmit(onSubmitClicked, onError)}>
                <FlexGrow>
                  <RightAlign>
                    <Button
                      type="button" // Explicitly set type to "button"
                      onClick={() => {
                        setModalData({
                          type: "",
                          data: {
                            student_id: modalData?.data?.student_id,
                            program_id: "",
                            activity_date: new Date().toISOString(),
                            activity_type_id: 4,
                            expected_graduation_date: "",
                            expected_graduation_term_id: "",
                            honor_id: "",
                            with_transfer: false,
                            source_program_id: "",
                            source_program_name: "",
                          },
                        });
                      }}
                      isOutline
                      status={Status.primary}
                      style={{ padding: "4px 30px" }}
                    >
                      <Icon name="x-s" mr={0.5} />
                      Cancel
                    </Button>
                    <Button
                      type="button" // Explicitly set type to "button"
                      onClick={() => {
                        setIsPreviewOpen(false);
                        setModalData({
                          ...modalData,
                          data: {
                            ...modalData.data,
                            with_transfer: false,
                          },
                        });
                      }}
                      isOutline
                      status={Status.primary}
                      style={{
                        padding: "4px 12px",
                        marginLeft: "20px",
                        width: 110,
                      }}
                    >
                      <Icon name="arrow-left" mr={0.5} />
                      Back
                    </Button>
                    <Button
                      type="submit"
                      // disabled={
                      //   !modalData?.data?.program_id ||
                      //   !isValidDate ||
                      //   // !modalData?.data?.expected_graduation_date ||
                      //   !modalData?.data?.expected_graduation_term_id ||
                      //   !modalData?.data?.activity_type_id ||
                      //   !modalData?.data?.activity_date || //enromment date
                      //   !modalData?.data?.student_id ||
                      //   !isWithTransfer ||
                      //   !modalData?.data?.source_program_id
                      // }
                      onClick={() => handleSubmit(onSubmitClicked)}
                      status={Status.primary}
                      backgroundColor="#CB092B"
                      style={{ padding: "4px 12px", marginLeft: "8px" }}
                    >
                      Proceed With Transfer
                      <Icon name="arrow-right" ml={0.5} />
                    </Button>
                  </RightAlign>
                </FlexGrow>
              </form>
            </FlexBox>
          </FlexBox>
        </Modal>
      </>
    );
  }

  return (
    <>
      <SnackBar
        isOpen={errorSnackbarOpen}
        setOpen={setErrorSnackbarOpen}
        type="error"
        message={errorMessage}
      />
      <Modal
        isOpen={!!isOpen}
        setOpen={setOpen}
        customStyle={{
          content: { width: "100%", maxWidth: isMobile ? "100%" : "800px" },
        }}
      >
        <FlexBox
          style={{
            marginBottom: theme.spacing.base * 2,
          }}
        >
          <FlexGrow>
            <h3 style={{ marginRight: "auto", marginBottom: 0 }}>
              Add Program
            </h3>
          </FlexGrow>
          <RightAlign>
            <ClickableIcon
              name="x-s"
              onClick={() =>
                setModalData({
                  type: "",
                  data: {
                    student_id: modalData?.data?.student_id,
                    program_id: "",
                    activity_date: new Date().toISOString(),
                    activity_type_id: 4,
                    expected_graduation_date: "",
                    expected_graduation_term_id: "",
                    honor_id: "",
                    with_transfer: false,
                    source_program_id: "",
                    source_program_name: "",
                  },
                })
              }
            />
          </RightAlign>
        </FlexBox>

        {!loading ? (
          <form onSubmit={handleSubmit(onSubmitClicked, onError)}>
            <FlexBox
              alignItems="center"
              style={{
                marginBottom: theme.spacing.x2,
                marginTop: 10,
                paddingLeft: 10,
                paddingRight: 10,
              }}
            >
              <FlexGrow grow={2}>
                <StyledText color={"#000000"} fontWeight={"bold"}>
                  Program
                </StyledText>
              </FlexGrow>
              <FlexGrow grow={3}>
                <>
                  <StyledMinimalSelect
                    menuPosition="fixed"
                    menuPortalTarget={document.body}
                    styles={{
                      menuPortal: (base: any) => ({ ...base, zIndex: 9999 }),
                    }}
                    placeholder="Select program..."
                    options={programs}
                    onChange={(e: any) =>
                      setModalData({
                        type: "new",
                        data: {
                          ...modalData.data,
                          program_id: e.value,
                          program_name: e.label,
                        },
                      })
                    }
                    defaultValue={programs.find(
                      (p: any) => p.value === modalData?.data?.program_id
                    )}
                    style={{ width: "inherit" }}
                  />
                </>
              </FlexGrow>
            </FlexBox>
            <FlexBox
              alignItems="center"
              style={{
                marginBottom: theme.spacing.x2,
                paddingLeft: 10,
                paddingRight: 10,
              }}
            >
              <FlexGrow grow={2}>
                <StyledText color={"#000000"} fontWeight={"bold"}>
                  Enrollment Term
                </StyledText>
              </FlexGrow>
              <FlexGrow grow={3}>
                <>
                  <StyledMinimalSelect
                    // multi
                    menuPosition="fixed"
                    menuPortalTarget={document.body}
                    styles={{
                      menuPortal: (base: any) => ({ ...base, zIndex: 9999 }),
                    }}
                    placeholder="Select Term..."
                    options={terms}
                    onChange={(e: any) => {
                      setModalData({
                        type: "new",
                        data: {
                          ...modalData.data,
                          enrollment_term_id: e.value, // Save the formatted date
                        },
                      });
                    }}
                    defaultValue={terms.find(
                      (t: any) =>
                        t.value === modalData?.data?.expected_graduation_term_id
                    )}
                    style={{ width: "inherit" }}
                  />
                </>
              </FlexGrow>
            </FlexBox>

            <FlexBox
              alignItems="center"
              style={{
                marginBottom: theme.spacing.x2,
                paddingLeft: 10,
                paddingRight: 10,
              }}
            >
              <FlexGrow grow={2}>
                <StyledText color={"#000000"} fontWeight={"bold"}>
                  Expected Graduation Term
                </StyledText>
              </FlexGrow>
              <FlexGrow grow={3}>
                <>
                  <StyledMinimalSelect
                    // multi
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                    styles={{
                      menuPortal: (base: any) => ({ ...base, zIndex: 9999 }),
                    }}
                    placeholder="Select Term..."
                    options={terms}
                    onChange={(e: any) => {
                      setModalData({
                        type: "new",
                        data: {
                          ...modalData.data,
                          expected_graduation_term_id: e.value, // Save the formatted date
                        },
                      });
                    }}
                    defaultValue={terms.find(
                      (t: any) =>
                        t.value === modalData?.data?.expected_graduation_term_id
                    )}
                    style={{ width: "inherit" }}
                  />
                </>
              </FlexGrow>
            </FlexBox>
            <FlexBox>
              <FlexGrow grow={2}>
                <StyledText color={"#000000"} fontWeight={"bold"}>
                  <Checkbox
                    // style={{ paddingRight: 10 }}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      setIsWithTransfer(e.target.checked);
                    }}
                    checked={isWithTransfer}
                    inputProps={{ "aria-label": "controlled" }}
                  />{" "}
                  Transfer credits from program
                </StyledText>
              </FlexGrow>
              {isWithTransfer && (
                <FlexGrow
                  grow={3}
                  style={{ paddingLeft: 10, paddingRight: 10 }}
                >
                  <>
                    <StyledMinimalSelect
                      menuPortalTarget={document.body}
                      menuPosition="fixed"
                      styles={{
                        menuPortal: (base: any) => ({ ...base, zIndex: 9999 }),
                      }}
                      placeholder="Select source program..."
                      options={mapUserPrograms()}
                      onChange={(e: any) =>
                        setModalData({
                          type: "new",
                          data: {
                            ...modalData.data,
                            source_program_id: e.value,
                            source_program_name: e.label,
                          },
                        })
                      }
                      value={mapUserPrograms().find(
                        (p: any) =>
                          p.value === modalData?.data?.source_program_id
                      )}
                      style={{ width: "inherit" }}
                    />
                  </>
                </FlexGrow>
              )}
            </FlexBox>

            {/* <FlexBox
              alignItems="center"
              style={{
                marginBottom: theme.spacing.x2,
              }}
            >
              <FlexGrow grow={1}>
                <StyledText color={"#000000"} fontWeight={"bold"}>
                  Honor
                </StyledText>
              </FlexGrow>
              <FlexGrow grow={2}>
                <>
                  <StyledMinimalSelect
                    // multi
                    menuPortalTarget={document.body}
                    styles={{
                      menuPortal: (base: any) => ({ ...base, zIndex: 9999 }),
                    }}
                    placeholder="Select Honor Status..."
                    options={honors}
                    onChange={(e: any) => {
                      setModalData({
                        type: "new",
                        data: {
                          ...modalData.data,
                          honor_id: e.value, // Save the formatted date
                        },
                      });
                    }}
                    defaultValue={""}
                    style={{ width: "inherit" }}
                  />
                </>
              </FlexGrow>
            </FlexBox> */}

            <FlexBox
              alignItems="center"
              justify="flex-end"
              style={{
                marginTop: theme.spacing.x4,
                marginBottom: theme.spacing.x2,
              }}
            >
              <FlexGrow>
                <RightAlign>
                  <Button
                    type="button" // Explicitly set type to "button"
                    onClick={() => {
                      setModalData({
                        type: "",
                        data: {
                          student_id: modalData?.data?.student_id,
                          program_id: "",
                          activity_date: new Date().toISOString(),
                          activity_type_id: 4,
                          expected_graduation_date: "",
                          expected_graduation_term_id: "",
                          honor_id: "",
                          with_transfer: false,
                          source_program_id: "",
                          source_program_name: "",
                        },
                      });
                    }}
                    isOutline
                    status={Status.primary}
                    style={{ padding: "4px 30px" }}
                  >
                    <Icon name="x-s" mr={0.5} />
                    Cancel
                  </Button>
                  <Button
                    // isOutline
                    type="submit"
                    disabled={
                      !modalData?.data?.program_id ||
                      !isValidDate ||
                      // !modalData?.data?.expected_graduation_date ||
                      !modalData?.data?.expected_graduation_term_id ||
                      !modalData?.data?.activity_type_id ||
                      !modalData?.data?.activity_date || //enromment date
                      !modalData?.data?.student_id ||
                      !isWithTransfer ||
                      !modalData?.data?.source_program_id
                    }
                    onClick={() => openPreviewModal()}
                    // onClick={() => handleSubmit(onSubmitClicked)}
                    status={Status.primary}
                    // backgroundColor="#CB092B"
                    style={{ padding: "4px 12px", marginLeft: "8px" }}
                  >
                    <Icon name="rotate-right" mr={0.5} />
                    Add Program With Transfers
                  </Button>
                  <Button
                    type="submit"
                    disabled={
                      !modalData?.data?.program_id ||
                      !isValidDate ||
                      // !modalData?.data?.expected_graduation_date ||
                      !modalData?.data?.expected_graduation_term_id ||
                      !modalData?.data?.activity_type_id ||
                      !modalData?.data?.activity_date || //enromment date
                      !modalData?.data?.student_id ||
                      isWithTransfer
                    }
                    onClick={() => handleSubmit(onSubmitClicked)}
                    status={Status.primary}
                    backgroundColor="#CB092B"
                    style={{ padding: "4px 12px", marginLeft: "8px" }}
                  >
                    <Icon name="plus" mr={0.5} />
                    Add Program
                  </Button>
                </RightAlign>
              </FlexGrow>
            </FlexBox>
          </form>
        ) : (
          <Icon mr={1} name="spinner" spin />
        )}
      </Modal>
    </>
  );
};

export default AddProgramModal;
